import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "@phosphor-icons/react";
import { CoverageAckReason } from "./CoverageAckReason";
import { reviewUnresolvedCount } from "../lib/filingReadiness";

// v0.5.9f — STAGE5B right slide-over drawer. Holds the full detail for a
// single readiness gate (all the content the old stacked banners carried).
// Accessibility (addition #3): Esc closes, focus is moved into the drawer on
// open and restored to the invoking chip on close, backdrop click closes.
const GATE_META = {
  reconciliation_ok: { dot: "bg-teal-500", titleKey: "ui.filing.status.reconciliation", titleDefault: "Reconciliation" },
  review_flags_ok: { dot: "bg-teal-500", titleKey: "ui.filing.status.review", titleDefault: "Review" },
  coverage_ok: { dot: "bg-teal-500", titleKey: "ui.filing.status.coverage", titleDefault: "Coverage" },
  needs_input_ok: { dot: "bg-amber-400", titleKey: "ui.filing.status.needs_input_title", titleDefault: "Needs input" },
};

const GapAckInline = ({ gap, onAck }) => {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  const [preset, setPreset] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (note.trim().length < 10) return;
    setBusy(true);
    try { await onAck(gap, note.trim()); } finally { setBusy(false); }
  };
  // v0.6.12 A5-27 — same picker as RegistrationCoverageBanner's popover
  // (one act, one list).
  return (
    <div className="mt-2">
      <CoverageAckReason
        gap={gap}
        preset={preset}
        note={note}
        onChange={(p, v) => { setPreset(p); setNote(v); }}
        presetTestid={`drawer-coverage-ack-preset-${gap.gap_key}`}
        noteTestid={`drawer-coverage-ack-note-${gap.gap_key}`}
        rows={2}
        placeholder={t("ui.filing.coverage.drawer_ack_placeholder", { defaultValue: "Explain why filing without this registration is correct (min 10 chars)…" })}
      />
      <button
        type="button"
        data-testid={`drawer-coverage-ack-submit-${gap.gap_key}`}
        disabled={note.trim().length < 10 || busy}
        onClick={submit}
        className="mt-1.5 text-[11px] px-3 py-1 bg-teal-700 text-white rounded-md disabled:opacity-40"
      >
        {busy ? t("ui.filing.status.saving", { defaultValue: "Saving…" }) : t("ui.review_grouped.row_resolve_submit", { defaultValue: "Acknowledge & keep" })}
      </button>
    </div>
  );
};

export const FilingStatusDrawer = ({
  openGate, onClose, verdict, recon, review, coverage,
  needsInputItems, onNavigateBox, onAckCoverageGap,
}) => {
  const { t } = useTranslation();
  const closeRef = useRef(null);
  const restoreRef = useRef(null);
  const open = !!openGate;

  useEffect(() => {
    if (!open) return undefined;
    restoreRef.current = document.activeElement;
    const el = closeRef.current;
    const focusT = window.setTimeout(() => el?.focus(), 60);
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(focusT);
      document.removeEventListener("keydown", onKey);
      const r = restoreRef.current;
      if (r && typeof r.focus === "function") r.focus();
    };
  }, [open, onClose]);

  const meta = openGate ? GATE_META[openGate] : null;
  const ok = openGate ? verdict?.gates?.[openGate] : true;
  const dot = ok
    ? "bg-teal-500"
    : (openGate === "needs_input_ok" ? "bg-amber-400" : "bg-rose-500");

  return (
    <>
      <div
        data-testid="filing-drawer-backdrop"
        onClick={onClose}
        className={`fixed inset-0 bg-slate-900/30 z-40 transition-opacity ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      />
      <aside
        data-testid="filing-status-drawer"
        data-open={open ? "true" : "false"}
        role="dialog"
        aria-modal="true"
        aria-label={meta ? t(meta.titleKey, { defaultValue: meta.titleDefault }) : "Filing status detail"}
        className={`fixed top-0 right-0 h-full w-[420px] max-w-[90vw] bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700 shadow-2xl z-50 transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${dot}`} />
            <h2 data-testid="filing-drawer-title" className="text-sm font-semibold text-slate-900 dark:text-slate-50">
              {meta ? t(meta.titleKey, { defaultValue: meta.titleDefault }) : ""}
            </h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            data-testid="filing-drawer-close"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-md p-1"
            aria-label={t("ui.common.close", { defaultValue: "Close" })}
          >
            <X size={18} />
          </button>
        </div>
        <div data-testid="filing-drawer-body" className="p-5 text-[13px] text-slate-600 dark:text-slate-300 space-y-3 overflow-y-auto h-[calc(100%-57px)]">
          {openGate === "reconciliation_ok" && (
            ok ? (
              <>
                <p className="text-teal-700 dark:text-teal-300">✓ {t("ui.filing.status.recon_pass", { defaultValue: "Calc reconciliation (R1–R4) passed." })}</p>
                <p className="text-[12px] text-slate-500 dark:text-slate-400">{t("ui.filing.status.recon_counts", { passed: recon?.rule_checks_passed ?? 0, failed: recon?.rule_checks_failed ?? 0, defaultValue: `${recon?.rule_checks_passed ?? 0} checks passed · ${recon?.rule_checks_failed ?? 0} failed` })}</p>
              </>
            ) : (
              <>
                <p className="text-rose-700 dark:text-rose-300 font-medium">⚠ {t("ui.filing.status.recon_fail", { defaultValue: "Reconciliation failed — do not file until resolved." })}</p>
                <ul className="text-[12px] space-y-1 mt-2" data-testid="filing-drawer-recon-deltas">
                  {(recon?.deltas || []).map((d, i) => (
                    <li key={i} className="text-rose-700 dark:text-rose-300">
                      {d?.label || t("ui.filing.status.check_n", { n: i + 1, defaultValue: `Check ${i + 1}` })}
                      {d?.expected !== undefined && d?.actual !== undefined
                        ? t("ui.filing.status.expected_got", { expected: d.expected, actual: d.actual, defaultValue: ` — expected ${d.expected}, got ${d.actual}` })
                        : ""}
                    </li>
                  ))}
                </ul>
              </>
            )
          )}
          {openGate === "review_flags_ok" && (
            <>
              <p className={ok ? "text-teal-700 dark:text-teal-300" : "text-rose-700 dark:text-rose-300 font-medium"}>
                {ok ? "✓ " : "⚠ "}
                {ok
                  ? t("ui.filing.status.review_pass", { defaultValue: "All flagged rows reviewed (0 blocking)." })
                  : t("ui.filing.status.review_fail", { count: reviewUnresolvedCount(review), defaultValue: `${reviewUnresolvedCount(review)} blocking review flag(s) unresolved.` })}
              </p>
              <ul className="text-[12px] text-slate-500 dark:text-slate-400 space-y-1 mt-2" data-testid="filing-drawer-review-groups">
                {(review?.groups || []).map((g) => (
                  <li key={g.flag_type}>
                    {g.count} · {g.flag_type} · {(g.acknowledged_count || 0) >= g.count ? t("ui.filing.status.acknowledged", { defaultValue: "acknowledged" }) : `${g.acknowledged_count || 0}/${g.count} ${t("ui.filing.status.acknowledged", { defaultValue: "acknowledged" })}`}
                  </li>
                ))}
                {(review?.groups || []).length === 0 && (
                  <li>{t("ui.filing.status.review_none", { defaultValue: "No flagged rows in this calculation." })}</li>
                )}
              </ul>
            </>
          )}
          {openGate === "coverage_ok" && (
            <div className="space-y-3">
              {ok ? (
                <p className="text-teal-700 dark:text-teal-300">✓ {t("ui.filing.status.coverage_pass", { defaultValue: "Every liability covered by a registration." })}</p>
              ) : (
                <p className="text-rose-700 dark:text-rose-300 font-medium">⚠ {t("ui.filing.status.coverage_fail", { count: coverage?.open_gap_count || 0, defaultValue: `${coverage?.open_gap_count || 0} registration-coverage gap(s) to acknowledge.` })}</p>
              )}
              <ul className="space-y-2" data-testid="filing-drawer-coverage-gaps">
                {(coverage?.gaps || []).map((g) => (
                  <li key={g.gap_key} data-testid={`drawer-coverage-gap-${g.gap_key}`} className={`border rounded-lg p-2 leading-snug ${g.acknowledged ? "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300" : "border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/30 text-rose-800 dark:text-rose-200"}`}>
                    <div><span className="font-semibold tabular-nums">{g.country || "OSS"}</span> · {g.message}</div>
                    {g.acknowledged
                      ? <div className="text-[11px] text-teal-700 dark:text-teal-300 mt-0.5">✓ {g.ack_by}: “{g.ack_note}”</div>
                      : <GapAckInline gap={g} onAck={onAckCoverageGap} />}
                  </li>
                ))}
              </ul>
              {(coverage?.covered || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="filing-drawer-coverage-covered">
                  {(coverage?.covered || []).map((c) => (
                    <span key={c.country} className="text-[10px] px-2 py-0.5 rounded bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border border-teal-200 dark:border-teal-800">
                      {c.country} · {t("ui.filing.status.local_reg", { defaultValue: "local reg" })} ✓
                    </span>
                  ))}
                </div>
              )}
              {coverage?.msi_mismatch && (
                <div className="text-[11px] text-amber-700 dark:text-amber-300">{coverage.msi_mismatch.message}</div>
              )}
            </div>
          )}
          {openGate === "needs_input_ok" && (
            ok ? (
              <p className="text-teal-700 dark:text-teal-300">✓ {t("ui.filing.status.needs_input_pass", { defaultValue: "All required manual-input boxes entered." })}</p>
            ) : (
              <>
                <p className="leading-snug">
                  {t("ui.filing.status.needs_input_body", { defaultValue: "Required manual-input box(es) below — enter a value (0 is fine, with a reason). Uploaded purchases fill it in once their questions are answered; without purchases the engine can't know your input VAT. Leaving it empty would silently zero your deduction." })}
                </p>
                <ul className="space-y-2 mt-2" data-testid="filing-drawer-needs-input-list">
                  {needsInputItems.map((it) => (
                    <li key={`${it.country}-${it.box_id}`}>
                      <button
                        type="button"
                        data-testid={`filing-drawer-needs-input-${it.country}-${it.box_id}`}
                        onClick={() => onNavigateBox(it)}
                        className="w-full inline-flex items-center gap-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/50 text-left"
                      >
                        <span className="tabular-nums text-[11px] rounded bg-amber-200/60 dark:bg-amber-800/50 px-1.5 py-0.5">{it.country} · Box {it.box_id}</span>
                        <span className="flex-1">{it.label}</span>
                        <span aria-hidden>→</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )
          )}
        </div>
      </aside>
    </>
  );
};

export default FilingStatusDrawer;
